import {useThread} from "@/Store/Thread.js";
import {storeToRefs} from "pinia";
import {router, usePage} from "@inertiajs/vue3";
import {loadLanguageAsync, trans} from "laravel-vue-i18n";
import dayjs from "dayjs";
import {useUser} from "@/Store/User.js";
import {computed, onMounted} from "vue";

/**
 * A dynamic import (Inertia page resolution, the pusher-js/laravel-echo imports below, the
 * emoji-picker import) fetches a chunk by its build hash; once a deploy replaces the build, a
 * tab left open from before the deploy asks for a hash that no longer exists on the server and
 * fails with "Failed to fetch dynamically imported module" and friends. Vite dispatches
 * `vite:preloadError` for exactly this case - reloading re-fetches the current build's manifest
 * and recovers, so this must run once, as early as possible, before any dynamic import can fail.
 */
export const registerChunkErrorRecovery = () => {
    window.addEventListener('vite:preloadError', (event) => {
        event.preventDefault()
        window.location.reload()
    })
}

export const getPropertyName = (data) => {
    if (data.property.type === 'list') {
        return `${data.property.title}: ${data.property_value?.value}`
    } else if (data.property.type === 'bool') {
        return `${data.property.title}: ${data.value ? trans('Yes') : trans('No')}`
    } else {
        return `${data.property.title}: ${data.value}`
    }
}

export const truncateEmail = (email) => {
    if (email.length > 32) {

        let strData = email.split('@'),
            nameEmail = strData[0],
            typeEmail = strData[1]
        if (nameEmail.length > 14) {
            let half = Math.floor(14 / 2);
            nameEmail = nameEmail.substr(0, half) + "..." + nameEmail.substr(-half);
            let arrTypeEmailStr = typeEmail.split('.')
            if (arrTypeEmailStr[0].length > 6) {
                let half = Math.floor(6 / 2);
                typeEmail = `${arrTypeEmailStr[0].substr(0, half)}...${arrTypeEmailStr[0].substr(-half)}.${arrTypeEmailStr[1]}`;
            }
            return nameEmail + "@" + typeEmail

        } else {
            return email;
        }
    } else {
        return email;
    }

}

export const setMessages = (msgs) => {
    const storeThread = useThread(),
        {messages} = storeToRefs(storeThread)

    messages.value = Object.fromEntries(
        msgs.map(item => [item.id, item])
    );

}

export const listenerNavigate = () => {
    router.reload({
        only: ['messages'],
        onFinish: () => {
            setMessages(usePage().props.messages?.data ?? [])
        }
    })
}

export const onSelectLang = async (newLang) => {
    await loadLanguageAsync(newLang.code)
    dayjs.locale(newLang.code)
    router.post(route('locale'), {
        locale: newLang.code
    }, {preserveScroll: true, preserveState: false})
}

export const onSelectCurrency = (newCurrency) => {
    router.post(route('currency'), {
        currency: newCurrency.code.toLowerCase()
    }, {preserveScroll: true, preserveState: false})
}

export const activeCurrency = computed(() => usePage().props.currency)

export const normalizePriceStr = (val, precision = 2) => {
    const newVal = val.toString().replace(/\.+/g, ',')
        .replace(/,+/g, ',')
        .replace(/[^0-9,]/g, '')
    const parts = newVal.split(',')
    if (parts.length > 1 && parts[1].length > precision) {
        parts[1] = parts[1].slice(0, precision)
    }
    if (parts.length >= 2) {
        return parts[0] + ',' + parts.slice(1).join('')
    }
    return newVal
}

export const priceNumToStr = (price, precision = 2) => {
    return price.toFixed(precision).replace(/\./g, ',')
}

export const priceStrToNum = (price) => {
    const val = price.replace(/,+/g, '.')
        .replace(/\.+/g, '.')
        .replace(/[^0-9.]/g, '')
    const parts = val.toString().split('.')
    if (parts.length > 2) {
        return Number(parts[0] + '.' + parts.slice(1).join(''))
    }
    return Number(val)
}

export const getLocalStorageItem = (key) => {
    if (typeof window === 'undefined') {
        return null
    }
    try {
        return window.localStorage.getItem(key)
    } catch {
        return null
    }
}

export const setLocalStorageItem = (key, value) => {
    if (typeof window === 'undefined') {
        return
    }
    try {
        window.localStorage.setItem(key, value)
    } catch {
        // Storage unavailable (private browsing, sandboxed context, etc.) - silently no-op,
        // matches getLocalStorageItem's fallback of returning null instead of throwing.
    }
}

export const getCookie = (name) => {
    if (typeof document === 'undefined') {
        return null
    }
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)'))
    return match ? decodeURIComponent(match[1]) : null
}

export const setCookie = (name, value, days) => {
    if (typeof document === 'undefined') {
        return
    }
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString()
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

const DAILY_GIVEAWAY_COOKIE = 'daily_giveaway_seen'

/**
 * Show the "Daily Giveaway" modal at most once per month. `showRef` is the boolean
 * ref bound to the modal's `show` prop; call this from the page's onMounted. A cookie
 * (30-day expiry) records that it was shown, so it won't reappear on every content
 * page view. SSR-safe: getCookie/setCookie no-op without `document`.
 */
export const showDailyGiveawayOnce = (showRef, delay = 2000) => {
    if (getCookie(DAILY_GIVEAWAY_COOKIE)) {
        return
    }
    setTimeout(() => {
        showRef.value = true
        setCookie(DAILY_GIVEAWAY_COOKIE, '1', 30)
    }, delay)
}

export const applyTheme = () => {
    const storeUser = useUser();
    onMounted(() => {
        const theme = getLocalStorageItem('color-scheme');
        if (theme) {
            storeUser.theme = theme;
            if (typeof document !== 'undefined') document.body.classList.add(storeUser.theme);
        }
    });
}

export const reachYaGoal = (targetName) => {
    try {
        // noinspection JSUnresolvedReference
        ym(96570129, 'reachGoal', targetName)
    } catch (e) {
        console.log(e);
    }
}

export const reachTikTokGoal = (targetName, content) => {
    try {
        ttq.track(targetName, content);
        console.log('TikTok track', targetName, content)
    } catch (e) {
        console.log(e);
    }
}


/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 */
/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allow your team to quickly build robust real-time web applications.
 */
/**
 * `reverbConfig` ({key, host, port, scheme}) comes from the `reverb` Inertia shared prop
 * (HandleInertiaRequests -> config('broadcasting.reverb_client')), computed server-side at
 * request time - not from import.meta.env, so the same built JS works unmodified against
 * whichever environment's Reverb instance is actually running (prod/stage1/CI each have their
 * own REVERB_HOST without needing a separate frontend build per environment).
 */
export function loadEcho(reverbConfig, callback) {
    if (typeof window.Echo !== 'undefined') {
        if (callback) {
            callback()
        }
        return
    }
    import('pusher-js').then(module => {
        // console.log('pusher-result', pusher)
        window.Pusher = module.default;
        import('laravel-echo').then(module => {
            // console.log('echo-result', module)
            const Echo = module.default;
            window.Echo = new Echo({
                broadcaster: 'reverb',
                key: reverbConfig?.key,
                wsHost: reverbConfig?.host,
                wsPort: reverbConfig?.port,
                wssPort: reverbConfig?.port,
                forceTLS: (reverbConfig?.scheme ?? 'https') === 'https',
                enabledTransports: ['ws', 'wss'],
            });
            if (callback) {
                callback()
            }
        })
    })
}
